import { useCallback } from 'react'
import type { UseMutationResult } from '@tanstack/react-query'
import type { Floor } from '../../../map-editor-shared/store/floorStore'
import type { ParkingLot } from '../../../map-editor-shared/store/projectStore'

interface UseServerSyncParams {
  projectId: number | null
  currentLot: string | null
  currentLotData: ParkingLot | null
  floors: Floor[]
  updateMapDataMutation: UseMutationResult<any, Error, { projectId: number; mapData: any }, unknown>
}

/**
 * Provides server sync functionality for map data.
 * Prepares floor data from localStorage and syncs to backend API.
 *
 * This is separate from local auto-save and runs on-demand (e.g., before floor switching).
 */
export const useServerSync = ({
  projectId,
  currentLot,
  currentLotData,
  floors,
  updateMapDataMutation,
}: UseServerSyncParams) => {
  // Auto-save to server (background sync)
  const autoSaveToServer = useCallback(async () => {
    if (!projectId || !currentLot || floors.length === 0) {
      return
    }

    try {
      // Prepare ALL floors data for API
      const projectFloors = floors.filter(f => f.lotId === currentLot)
      const floorsPayload = projectFloors.map(floor => ({
        name: floor.name,
        order: floor.order,
        graphJson: floor.mapData?.graphJson,
      })).filter(f => f.graphJson) // Only include floors with data

      if (floorsPayload.length === 0) {
        return
      }

      const mapDataPayload = {
        floors: floorsPayload,
        template_id: currentLotData?.templateId
      }


      // Update map data via API
      await updateMapDataMutation.mutateAsync({
        projectId,
        mapData: mapDataPayload
      })

    } catch (error) {
      // Don't throw - auto-save failures should be silent
    }
  }, [projectId, currentLot, currentLotData, floors, updateMapDataMutation])

  return { autoSaveToServer }
}
